import React, { useState } from 'react';
import royaleLogo from '../../assets/royale-logo.png';
import { SHADOWS } from '../../styles/UISystem';

// Import the UI system documentation directly
// Note: This requires raw-loader to be installed
import uiSystemContent from '!!raw-loader!../../docs/ui-system.md';

// Import UI System subpages
import ColorsScreen from './ColorsScreen';
import TypographyScreen from './TypographyScreen';
import IconsScreen from './IconsScreen';
import FormsScreen from './FormsScreen';
import ChatScreen from './ChatScreen';
import HeadersScreen from './HeadersScreen';
import ButtonsScreen from './ButtonsScreen';
import LayoutScreen from './LayoutScreen';
import DesignTokensScreen from './DesignTokensScreen';
import OverlaysScreen from './OverlaysScreen';
import CardsScreen from './CardsScreen';

/**
 * UI System Screen
 *
 * Displays the UI component documentation
 */
const UISystemScreen = () => {
  const [activeSection, setActiveSection] = useState(null);

  // Use the imported markdown content directly
  const markdownContent = uiSystemContent;

  // Parse the markdown content to extract sections
  const getSections = () => {
    if (!markdownContent) return [];

    // Find all section headers (# Section)
    const sectionRegex = /^# (.+)$/gm;
    const sections = ['Colors', 'Typography', 'Forms', 'Icons', 'Chat', 'Headers'];
    let match;

    while ((match = sectionRegex.exec(markdownContent)) !== null) {
      sections.push({
        name: match[1],
        startIndex: match.index,
      });
    }

    // Calculate the end index for each section
    return sections.map((section, index) => {
      const nextSection = sections[index + 1];
      const endIndex = nextSection ? nextSection.startIndex : markdownContent.length;

      return {
        ...section,
        content: markdownContent.slice(section.startIndex, endIndex),
      };
    });
  };

  // Get the content for a specific section
  const getSectionContent = (sectionName) => {
    const sections = getSections();
    const section = sections.find((s) => s.name.toLowerCase() === sectionName.toLowerCase());
    return section ? section.content : `No content found for ${sectionName}`;
  };

  // Handle navigation back to connections list
  const handleNavigateToQR = () => {
    console.log('Navigating back to connections list');
    window.dispatchEvent(
      new CustomEvent('navigate', {
        detail: {
          screen: 'profile-connections-list',
        },
      })
    );
  };

  // Render the main UI system navigation
  const renderMainNavigation = () => {
    return (
      <div
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen',
        }}
      >
        <div
          onClick={handleNavigateToQR}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px',
            cursor: 'pointer',
          }}
        >
          <img
            src={royaleLogo}
            alt="Royale Logo"
            style={{
              width: '48px',
              height: '48px',
              marginRight: '12px',
            }}
          />
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'normal',
              color: '#6D7074',
              margin: 0,
            }}
          >
            Royale UI
          </h1>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: SHADOWS.TIER1,
            marginTop: '20px',
          }}
        >
          {[
            { num: 1, name: 'Colors' },
            { num: 2, name: 'Typography' },
            { num: 3, name: 'Forms' },
            { num: 4, name: 'Chat' },
            { num: 5, name: 'Icons' },
            { num: 6, name: 'Headers' },
            { num: 7, name: 'Buttons' },
            { num: 8, name: 'Layout' },
            { num: 9, name: 'Design Tokens' },
            { num: 10, name: 'Overlays' },
            { num: 11, name: 'Cards' },
          ].map((item) => (
            <button
              key={item.name}
              onClick={() => setActiveSection(item.name)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 0',
                backgroundColor: 'transparent',
                color: '#333',
                textDecoration: 'none',
                fontWeight: 'normal',
                fontSize: '18px',
                textAlign: 'left',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                borderBottom: item.num === 9 ? 'none' : '1px solid #eee',
              }}
            >
              {`${item.num}. ${item.name}`}
            </button>
          ))}
        </div>

        {/* Removed 'Back to App' button as requested */}
      </div>
    );
  };

  // Render a specific section
  const renderSection = (sectionName) => {
    // Special cases for dedicated component sections
    if (sectionName === 'Colors') {
      return <ColorsScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Typography') {
      return <TypographyScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Icons') {
      return <IconsScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Forms') {
      return <FormsScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Chat') {
      return <ChatScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Headers') {
      return <HeadersScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Buttons') {
      return <ButtonsScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Layout') {
      return <LayoutScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Design Tokens') {
      return <DesignTokensScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Overlays') {
      return <OverlaysScreen onBack={() => setActiveSection(null)} />;
    }

    if (sectionName === 'Cards') {
      return <CardsScreen onBack={() => setActiveSection(null)} />;
    }

    // For other sections, use the default markdown content renderer
    const content = getSectionContent(sectionName);

    return (
      <div
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen',
        }}
      >
        <div
          onClick={handleNavigateToQR}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px',
            cursor: 'pointer',
          }}
        >
          <img
            src={royaleLogo}
            alt="Royale Logo"
            style={{
              width: '48px',
              height: '48px',
              marginRight: '12px',
            }}
          />
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'normal',
              color: '#6D7074',
              margin: 0,
            }}
          >
            Royale UI
          </h1>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: SHADOWS.TIER1,
            marginTop: '20px',
            whiteSpace: 'pre-wrap',
          }}
        >
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setActiveSection(null)}
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#F7F7F9',
                color: '#6D7074',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 'normal',
              }}
            >
              ← Back
            </button>
          </div>
          {content}
        </div>
      </div>
    );
  };

  return activeSection ? renderSection(activeSection) : renderMainNavigation();
};

export default UISystemScreen;
